import type {
  CalcTotals,
  MonthlyUtilityFormValues,
} from './interfaces/MonthlyRecord'

export const toYearMonth = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

export const monthStart = (ym: string): string => `${ym}-01`

export const monthEnd = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  return `${ym}-${String(last).padStart(2, '0')}`
}

const YEAR_MONTH_REGEX = /^\d{4}-\d{2}$/

export const getMonthFromSearchParams = (
  searchParams: URLSearchParams,
): string => {
  const month = searchParams.get('month')?.trim()
  return month && YEAR_MONTH_REGEX.test(month) ? month : toYearMonth(new Date())
}

/** 4-digit meter max value (0000–9999, wraps at 10000) */
export const METER_MAX = 10000

/**
 * Returns true if curr >= prev (normal) OR the reading rolled over past 9999
 * (detected when the backward gap is more than half the meter range).
 * e.g. prev=9967, curr=0003  → gap=9964 > 5000 → valid rollover
 *      prev=7777, curr=6666  → gap=1111 ≤ 5000 → invalid
 */
export const isMeterReadingValid = (prev: number, curr: number): boolean =>
  curr >= prev || prev - curr > METER_MAX / 2

/** Units consumed, accounting for 4-digit rollover. */
export const calcMeterUnits = (prev: number, curr: number): number => {
  if (curr >= prev) return curr - prev
  if (prev - curr > METER_MAX / 2) return METER_MAX - prev + curr
  return 0 // invalid reading – caller should surface an error
}

export const calcTotals = (
  form: MonthlyUtilityFormValues,
  _room?: {
    price_monthly?: number
    internet_fee?: number
    furniture_fee?: number
    maintenance_fee?: number
    other_fee?: number
  },
): CalcTotals => {
  const elecPrev = Number(form.elec_prev_reading) || 0
  const elecCurr = Number(form.elec_curr_reading) || 0
  const elecPrice = Number(form.elec_unit_price) || 0
  const waterPrev = Number(form.water_prev_reading) || 0
  const waterCurr = Number(form.water_curr_reading) || 0
  const waterPrice = Number(form.water_unit_price) || 0

  const elec_units =
    form.elec_curr_reading === '' ? 0 : calcMeterUnits(elecPrev, elecCurr)
  const elec_total = elec_units * elecPrice

  const water_units =
    form.water_curr_reading === '' ? 0 : calcMeterUnits(waterPrev, waterCurr)
  const water_total = water_units * waterPrice

  const grand_total =
    (Number(form.price_monthly) || 0) +
    elec_total +
    water_total +
    (Number(form.internet_fee) || 0) +
    (Number(form.furniture_fee) || 0) +
    (Number(form.maintenance_fee) || 0) +
    (Number(form.other_fee) || 0)

  return { elec_units, elec_total, water_units, water_total, grand_total }
}
